import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { useStyles } from '../../../common/hooks/useStyles';
import { getUpNextId } from '../htmlElements/htmlElementsIdGetter';
import { getUpNextStyleObject } from './upNext.style';
import {
  replayVideo,
  updateShowUpNext,
} from '../store/discoveryPlugin.actions';
import { discoverySelector } from '../../../common/store/selectors/discovery.selector';
import { DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS } from '../discoveryPlugin.constants';
import { NextVideo } from '../nextVideo/nextVideo';
import { css } from '../../../common/styles/styles.utils';
import { mediaDataSelector } from '../../../common/store/selectors/mediaData.selector';
import { moreVideosSelector } from '../../../common/store/selectors/moreVideos.selector';

export const UpNext: FunctionComponent = () => {
  const dispatch = useDispatch();
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqId);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const showUpNext = useSelector(discoverySelector.showUpNext);
  const currentVideoDuration = useSelector(playerDataSelector.currentVideoDuration);
  const currentVideoTime = useSelector(playerDataSelector.currentVideoTime);
  const currentVideoTimeFragment = useSelector(playerDataSelector.currentVideoTimeFragment);
  const videoList = useSelector(mediaDataSelector.videoList);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const galleryView = useSelector(moreVideosSelector.galleryView);
  const moreVideosInteractionOnEndState = useSelector(moreVideosSelector.interactionOnEndState);

  const [isClosedUpNext, setIsClosedUpNext] = useState(false);
  const [endStateCurrentTime, setEndStateCurrentTime] = useState(0);
  const [endOfCountdown, setEndOfCountdown] = useState(false);

  const isEndState = videoList.length === 1 && videoTagStatus === 'complete';
  const countdownTimeUpNext = showUpNext ? DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS - (currentVideoDuration - currentVideoTimeFragment) : 0;
  const countdownTime = isEndState ? endStateCurrentTime : countdownTimeUpNext;

  const styles = useStyles(getUpNextStyleObject, [playerSize, showUpNext, isEndState, galleryView]);
  const { upNextContainerStyle } = styles;

  useEffect(() => {
    if (currentVideoTime >= Math.floor(currentVideoDuration - DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS) && currentVideoTime !== 0 &&
      currentVideoDuration !== 0 && !isClosedUpNext) {
      dispatch(updateShowUpNext(true));
    }
    if (currentVideoTime < Math.floor(currentVideoDuration - DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS)) {
      setEndOfCountdown(false);
      dispatch(updateShowUpNext(false));
    }
  }, [currentVideoTime, currentVideoDuration, dispatch, isClosedUpNext]);

  useEffect(() => {
    if (currentVideoTimeFragment === currentVideoDuration && !isEndState) {
      dispatch(updateShowUpNext(false));
      setIsClosedUpNext(false);
    }
  }, [currentVideoDuration, currentVideoTimeFragment, isEndState, dispatch]);

  useEffect(() => {
    let countdown;
    if (isEndState && !isClosedUpNext && !endOfCountdown) {
      if (!isClosedUpNext) {
        dispatch(updateShowUpNext(true));
      }
      if (endStateCurrentTime < DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS) {
        countdown = setTimeout(() => {
          setEndStateCurrentTime(endStateCurrentTime + 0.2);
        }, 200);
      }
      if (moreVideosInteractionOnEndState) {
        clearTimeout(countdown);
        setEndStateCurrentTime(0);
      }
      if (endStateCurrentTime >= DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS) {
        setEndOfCountdown(true);
        dispatch(replayVideo());
        setEndStateCurrentTime(0);
        dispatch(updateShowUpNext(false));
      }
    }
    return (): void => clearTimeout(countdown);
  }, [endStateCurrentTime, isClosedUpNext, isEndState, endOfCountdown, dispatch, moreVideosInteractionOnEndState]);

  return (
    <div id={getUpNextId(playerUniqueId)} style={css(upNextContainerStyle)}>
      <NextVideo setIsClosedUpNext={setIsClosedUpNext} countdownTime={countdownTime} />
    </div>
  );
};
